import React from "react"
import { useTranslations } from "@/hooks/useTranslations"
import { useLocaleStore } from "@/stores/localeStore"
import { useAuth } from "@/hooks/useAuth"
import { Globe } from "lucide-react"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"

interface LanguageSelectorProps {
	compact?: boolean
	className?: string
}

const languages = [
	{ value: "es", label: "Español" },
	{ value: "ca", label: "Català" },
	{ value: "en", label: "English" },
]

/**
 * Unified Language Selector Component
 *
 * Uses a Select dropdown matching the OptimGov design system.
 * Persists language choice to database when user is logged in.
 */
export const LanguageSelector: React.FC<LanguageSelectorProps> = ({ compact = false, className = "" }) => {
	const t = useTranslations()
	const { locale } = useLocaleStore()
	const { user, updateUserPreferences } = useAuth()

	const handleLanguageChange = async (newLocale: string) => {
		const typedLocale = newLocale as "es" | "ca" | "en"

		// Update store immediately for responsive UX
		const { setLocale } = useLocaleStore.getState()
		setLocale(typedLocale)

		// Persist to database if user is logged in
		if (user) {
			try {
				await updateUserPreferences({ locale: typedLocale })
			} catch (error) {
				console.error("Failed to update user locale:", error)
			}
		}
	}

	const currentLanguage = languages.find((lang) => lang.value === locale)

	if (compact) {
		return (
			<div className={`flex items-center gap-2 ${className}`}>
				<Select value={locale} onValueChange={handleLanguageChange}>
					<SelectTrigger className='w-[120px] h-9 bg-white border-gray-200 hover:bg-gray-50'>
						<div className='flex items-center gap-2'>
							<Globe size={16} className='text-brand-forest-start' />
							<SelectValue>{currentLanguage && <span className='text-sm font-medium'>{currentLanguage.label}</span>}</SelectValue>
						</div>
					</SelectTrigger>
					<SelectContent>
						{languages.map((lang) => (
							<SelectItem key={lang.value} value={lang.value}>
								{lang.label}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>
		)
	}

	return (
		<div className={`relative ${className}`}>
			<label className='block text-sm font-medium text-gray-700 mb-2'>
				<Globe className='inline w-4 h-4 mr-2' />
				{t("settings.language")}
			</label>
			<Select value={locale} onValueChange={handleLanguageChange}>
				<SelectTrigger className='w-full bg-white border-gray-200 hover:bg-gray-50'>
					<div className='flex items-center gap-2'>
						<Globe size={16} className='text-brand-forest-start' />
						<SelectValue>{currentLanguage && <span className='text-sm font-medium'>{currentLanguage.label}</span>}</SelectValue>
					</div>
				</SelectTrigger>
				<SelectContent>
					{languages.map((lang) => (
						<SelectItem key={lang.value} value={lang.value}>
							{lang.label}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
		</div>
	)
}
